import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import api from '../api/axios';
import AgentLayout from './AgentLayout';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN') : '—');

const fmtINR = (n) => (n ? '₹' + Number(n).toLocaleString('en-IN') : '—');

// User types "example.com" (no protocol) — this adds "https://" before saving.
const normalizeWebsite = (value) => {
  const v = (value || '').trim();
  if (!v) return v;
  return /^https?:\/\//i.test(v) ? v : `https://${v}`;
};

const JOB_STATUS_OPTIONS = ['active', 'paused', 'closed', 'draft'];
const WORK_MODE_OPTIONS = ['work_from_home', 'hybrid', 'office'];
const WORK_SHIFT_OPTIONS = ['day', 'night'];
const JOB_TYPE_OPTIONS = ['full_time', 'part_time', 'contract', 'internship'];

// Debounce delay (ms) before firing an AI suggestion request while typing.
const SUGGEST_DEBOUNCE_MS = 350;

function AgentEmployerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [employer, setEmployer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [forbidden, setForbidden] = useState(false);

  const [appsModalCandidate, setAppsModalCandidate] = useState(null);
  const [applications, setApplications] = useState([]);
  const [appsLoading, setAppsLoading] = useState(false);
  const [appsError, setAppsError] = useState(false);

  // ---- Job edit modal state ----
  const [editJob, setEditJob] = useState(null); // job object currently being edited
  const [editForm, setEditForm] = useState(null); // form field values
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState(false);

  // ---- Job delete confirm state ----
  const [deleteJob, setDeleteJob] = useState(null); // job object pending delete confirmation
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(false);

  // ---- Employer details view/edit modal state ----
const [viewingEmployer, setViewingEmployer] = useState(false);
const [editEmployer, setEditEmployer] = useState(false);
const [editEmployerForm, setEditEmployerForm] = useState(null);
const [editEmployerSaving, setEditEmployerSaving] = useState(false);
const [editEmployerError, setEditEmployerError] = useState(false);
const [convertToCandidateOpen, setConvertToCandidateOpen] = useState(false);
const [convertingToCandidate, setConvertingToCandidate] = useState(false);
const [convertToCandidateNote, setConvertToCandidateNote] = useState('');
const [convertToCandidateError, setConvertToCandidateError] = useState(false);
// ---- AI city suggestions for the employer edit form ----
const [citySuggestions, setCitySuggestions] = useState([]);
const [cityLoading, setCityLoading] = useState(false);
const [showCitySuggestions, setShowCitySuggestions] = useState(false);
const cityDebounceRef = useRef(null);

// ---- AI industry suggestions ----
const [industrySuggestions, setIndustrySuggestions] = useState([]);
const [industryLoading, setIndustryLoading] = useState(false);
const [showIndustrySuggestions, setShowIndustrySuggestions] = useState(false);
const industryDebounceRef = useRef(null);

  // ---- AI suggestions for the job edit form: Title, Company Name, Location, Skills ----
  const [jobTitleSuggestions, setJobTitleSuggestions] = useState([]);
  const [jobTitleLoading, setJobTitleLoading] = useState(false);
  const [showJobTitleSuggestions, setShowJobTitleSuggestions] = useState(false);
  const jobTitleDebounceRef = useRef(null);

  const [jobCompanySuggestions, setJobCompanySuggestions] = useState([]);
  const [jobCompanyLoading, setJobCompanyLoading] = useState(false);
  const [showJobCompanySuggestions, setShowJobCompanySuggestions] = useState(false);
  const jobCompanyDebounceRef = useRef(null);

  const [jobLocationSuggestions, setJobLocationSuggestions] = useState([]);
  const [jobLocationLoading, setJobLocationLoading] = useState(false);
  const [showJobLocationSuggestions, setShowJobLocationSuggestions] = useState(false);
  const jobLocationDebounceRef = useRef(null);

  const [jobSkillsSuggestions, setJobSkillsSuggestions] = useState([]);
  const [jobSkillsLoading, setJobSkillsLoading] = useState(false);
  const [showJobSkillsSuggestions, setShowJobSkillsSuggestions] = useState(false);
  const jobSkillsDebounceRef = useRef(null);

  useEffect(() => {
    return () => {
      if (cityDebounceRef.current) clearTimeout(cityDebounceRef.current);
      if (industryDebounceRef.current) clearTimeout(industryDebounceRef.current);
      if (jobTitleDebounceRef.current) clearTimeout(jobTitleDebounceRef.current);
      if (jobCompanyDebounceRef.current) clearTimeout(jobCompanyDebounceRef.current);
      if (jobLocationDebounceRef.current) clearTimeout(jobLocationDebounceRef.current);
      if (jobSkillsDebounceRef.current) clearTimeout(jobSkillsDebounceRef.current);
    };
  }, []);

 

  const openApplications = async (candidate) => {
    setAppsModalCandidate(candidate);
    setApplications([]);
    setAppsError(false);
    setAppsLoading(true);
    try {
      const res = await api.get(`/agent/employers/${id}/candidates/${candidate.id}/applications`);
      setApplications(res.data);
    } catch (err) {
      console.error('Failed to load applications', err);
      setAppsError(true);
    } finally {
      setAppsLoading(false);
    }
  };

  const closeApplications = () => {
    setAppsModalCandidate(null);
    setApplications([]);
    setAppsError(false);
  };

  const loadEmployer = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    setNotFound(false);
    setForbidden(false);
    try {
      const res = await api.get(`/agent/employers/${id}`);
      setEmployer(res.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setNotFound(true);
      } else if (err.response?.status === 403) {
        setForbidden(true);
      } else {
        console.error('Failed to load employer', err);
        setLoadError(true);
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadEmployer();
  }, [loadEmployer]);

  // ---- Edit job handlers ----
  const openEditJob = (job) => {
    setEditJob(job);
    setEditError(false);
    // Mirrors every field AgentPostJob.jsx captures at creation time,
    // plus the management-only 'status' field.
    setEditForm({
      title: job.title || '',
      company_name: job.company_name || '',
      location: job.location || '',
      contact_number: job.contact_number || '',
      work_mode: job.work_mode || 'office',
      job_type: job.job_type || 'full_time',
      work_shift: job.work_shift || 'day',
      skills_required: job.skills_required || '',
      salary_min: job.salary_min ?? '',
      salary_max: job.salary_max ?? '',
      experience_required: job.experience_required ?? '',
      openings: job.openings ?? '',
      benefits: job.benefits || '',
      description: job.description || '',
      status: job.status || 'active',
    });
  };

  const closeEditJob = () => {
    setEditJob(null);
    setEditForm(null);
    setEditError(false);
    setJobTitleSuggestions([]);
    setShowJobTitleSuggestions(false);
    setJobCompanySuggestions([]);
    setShowJobCompanySuggestions(false);
    setJobLocationSuggestions([]);
    setShowJobLocationSuggestions(false);
    setJobSkillsSuggestions([]);
    setShowJobSkillsSuggestions(false);
  };

  const handleEditChange = (field) => (e) => {
    setEditForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  // ---- Job Title AI suggestions ----
  const handleJobTitleChange = (e) => {
    const value = e.target.value;
    setEditForm((prev) => ({ ...prev, title: value }));

    if (jobTitleDebounceRef.current) clearTimeout(jobTitleDebounceRef.current);

    if (value.trim().length < 2) {
      setJobTitleSuggestions([]);
      setShowJobTitleSuggestions(false);
      return;
    }

    jobTitleDebounceRef.current = setTimeout(async () => {
      setJobTitleLoading(true);
      try {
        const res = await api.post('/ai/suggest-keywords', { query: value.trim() });
        setJobTitleSuggestions(res.data?.suggestions || []);
        setShowJobTitleSuggestions(true);
      } catch (err) {
        console.error('Job title suggestion failed', err);
      } finally {
        setJobTitleLoading(false);
      }
    }, SUGGEST_DEBOUNCE_MS);
  };

  const pickJobTitleSuggestion = (value) => {
    setEditForm((prev) => ({ ...prev, title: value }));
    setShowJobTitleSuggestions(false);
    setJobTitleSuggestions([]);
  };

  // ---- Job Company Name AI suggestions ----
  const handleJobCompanyChange = (e) => {
    const value = e.target.value;
    setEditForm((prev) => ({ ...prev, company_name: value }));

    if (jobCompanyDebounceRef.current) clearTimeout(jobCompanyDebounceRef.current);

    if (value.trim().length < 2) {
      setJobCompanySuggestions([]);
      setShowJobCompanySuggestions(false);
      return;
    }

    jobCompanyDebounceRef.current = setTimeout(async () => {
      setJobCompanyLoading(true);
      try {
        const res = await api.post('/ai/suggest-companies', { query: value.trim() });
        setJobCompanySuggestions(res.data?.suggestions || []);
        setShowJobCompanySuggestions(true);
      } catch (err) {
        console.error('Job company suggestion failed', err);
      } finally {
        setJobCompanyLoading(false);
      }
    }, SUGGEST_DEBOUNCE_MS);
  };

  const pickJobCompanySuggestion = (value) => {
    setEditForm((prev) => ({ ...prev, company_name: value }));
    setShowJobCompanySuggestions(false);
    setJobCompanySuggestions([]);
  };

  // ---- Job Location AI suggestions ----
  const handleJobLocationChange = (e) => {
    const value = e.target.value;
    setEditForm((prev) => ({ ...prev, location: value }));

    if (jobLocationDebounceRef.current) clearTimeout(jobLocationDebounceRef.current);

    if (value.trim().length < 2) {
      setJobLocationSuggestions([]);
      setShowJobLocationSuggestions(false);
      return;
    }

    jobLocationDebounceRef.current = setTimeout(async () => {
      setJobLocationLoading(true);
      try {
        const res = await api.post('/ai/suggest-cities', { query: value.trim() });
        setJobLocationSuggestions(res.data?.suggestions || []);
        setShowJobLocationSuggestions(true);
      } catch (err) {
        console.error('Job location suggestion failed', err);
      } finally {
        setJobLocationLoading(false);
      }
    }, SUGGEST_DEBOUNCE_MS);
  };

  const pickJobLocationSuggestion = (value) => {
    setEditForm((prev) => ({ ...prev, location: value }));
    setShowJobLocationSuggestions(false);
    setJobLocationSuggestions([]);
  };

  // ---- Job Skills Required AI suggestions ----
  // The field holds a comma-separated string; we suggest based on the text
  // typed after the last comma, and append the picked suggestion to the list.
  const handleJobSkillsChange = (e) => {
    const value = e.target.value;
    setEditForm((prev) => ({ ...prev, skills_required: value }));

    if (jobSkillsDebounceRef.current) clearTimeout(jobSkillsDebounceRef.current);

    const parts = value.split(',');
    const currentToken = parts[parts.length - 1].trim();

    if (currentToken.length < 2) {
      setJobSkillsSuggestions([]);
      setShowJobSkillsSuggestions(false);
      return;
    }

    jobSkillsDebounceRef.current = setTimeout(async () => {
      setJobSkillsLoading(true);
      try {
        const existingSkills = parts.slice(0, -1).map((s) => s.trim()).filter(Boolean);
        const res = await api.post('/ai/suggest-skills', {
          query: currentToken,
          job_title: editForm?.title || undefined,
          existing_skills: existingSkills,
        });
        setJobSkillsSuggestions(res.data?.suggestions || []);
        setShowJobSkillsSuggestions(true);
      } catch (err) {
        console.error('Job skills suggestion failed', err);
      } finally {
        setJobSkillsLoading(false);
      }
    }, SUGGEST_DEBOUNCE_MS);
  };

  const pickJobSkillsSuggestion = (value) => {
    setEditForm((prev) => {
      const parts = (prev.skills_required || '').split(',');
      parts[parts.length - 1] = ` ${value}`;
      const rebuilt = parts
        .map((p, i) => (i === 0 ? p.trim() : p.trim()))
        .filter(Boolean)
        .join(', ');
      return { ...prev, skills_required: rebuilt + ', ' };
    });
    setShowJobSkillsSuggestions(false);
    setJobSkillsSuggestions([]);
  };

  const saveEditJob = async () => {
    if (!editJob) return;
    setEditSaving(true);
    setEditError(false);
    try {
      const res = await api.put(`/agent/jobs/${editJob.id}`, editForm);
      const updatedJob = res.data.job;
      setEmployer((prev) => ({
        ...prev,
        jobs: prev.jobs.map((j) => (j.id === editJob.id ? { ...j, ...updatedJob } : j)),
      }));
      closeEditJob();
    } catch (err) {
      console.error('Failed to update job', err);
      setEditError(true);
    } finally {
      setEditSaving(false);
    }
  };

  // ---- Delete job handlers ----
  const openDeleteJob = (job) => {
    setDeleteJob(job);
    setDeleteError(false);
  };

  const closeDeleteJob = () => {
    setDeleteJob(null);
    setDeleteError(false);
  };

  const confirmDeleteJob = async () => {
    if (!deleteJob) return;
    setDeleting(true);
    setDeleteError(false);
    try {
      await api.delete(`/agent/jobs/${deleteJob.id}`);
      setEmployer((prev) => ({
        ...prev,
        jobs: prev.jobs.filter((j) => j.id !== deleteJob.id),
      }));
      closeDeleteJob();
    } catch (err) {
      console.error('Failed to delete job', err);
      setDeleteError(true);
    } finally {
      setDeleting(false);
    }
  };


    // ---- View employer details ----
  const openViewEmployer = () => setViewingEmployer(true);
  const closeViewEmployer = () => setViewingEmployer(false);

  // ---- Edit employer details ----
  const openEditEmployer = () => {
    setEditEmployerError(false);
    setEditEmployerForm({
      name: employer.name || '',
      email: employer.email || '',
      phone: employer.phone || '',
      city: employer.city || '',
      company_industry: employer.company_industry || '',
      company_size: employer.company_size || '',
      company_website: employer.company_website || '',
      company_about: employer.company_about || '',
    });
    setEditEmployer(true);
  };

  // Arrived from the WhatsApp dropdown: open the edit form once.
  const waLocation = useLocation();
  const waOpened = useRef(false);
  useEffect(() => {
    if (employer && !waOpened.current && waLocation.state && waLocation.state.openEdit) {
      waOpened.current = true;
      openEditEmployer();
    }
  }, [employer]);

  const closeEditEmployer = () => {
    setEditEmployer(false);
    setEditEmployerForm(null);
    setEditEmployerError(false);
  };

  const handleEditEmployerChange = (field) => (e) => {
    setEditEmployerForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

    const fetchCitySuggestions = async (query) => {
    if (!query.trim() || query.trim().length < 2) {
      setCitySuggestions([]);
      return;
    }
    setCityLoading(true);
    try {
      const res = await api.post('/ai/suggest-cities', { query: query.trim() });
      setCitySuggestions(res.data.suggestions || []);
    } catch (err) {
      setCitySuggestions([]);
    } finally {
      setCityLoading(false);
    }
  };

  const handleCityInputChange = (e) => {
    const value = e.target.value;
    setEditEmployerForm((prev) => ({ ...prev, city: value }));
    setShowCitySuggestions(true);
    if (cityDebounceRef.current) clearTimeout(cityDebounceRef.current);
    cityDebounceRef.current = setTimeout(() => fetchCitySuggestions(value), 350);
  };

  const handleIndustryInputChange = (e) => {
  const value = e.target.value;
  setEditEmployerForm((prev) => ({ ...prev, company_industry: value }));
  if (industryDebounceRef.current) clearTimeout(industryDebounceRef.current);

  if (value.trim().length < 2) {
    setIndustrySuggestions([]);
    setShowIndustrySuggestions(false);
    return;
  }

  setShowIndustrySuggestions(true);
  industryDebounceRef.current = setTimeout(async () => {
    setIndustryLoading(true);
    try {
      const res = await api.post('/ai/suggest-industries', { query: value.trim() });
      setIndustrySuggestions(res.data?.suggestions || []);
    } catch (err) {
      setIndustrySuggestions([]);
    } finally {
      setIndustryLoading(false);
    }
  }, 350);
};

const selectIndustrySuggestion = (v) => {
  setEditEmployerForm((prev) => ({ ...prev, company_industry: v.trim() }));
  setIndustrySuggestions([]);
  setShowIndustrySuggestions(false);
};

  const selectCitySuggestion = (v) => {
  setEditEmployerForm((prev) => ({ ...prev, city: v.trim() }));
  setCitySuggestions([]);
  setShowCitySuggestions(false);
};


  const saveEditEmployer = async () => {
  setEditEmployerSaving(true);
  setEditEmployerError(false);
  try {
    const payload = {
      ...editEmployerForm,
      company_website: normalizeWebsite(editEmployerForm.company_website),
    };
    const res = await api.put(`/agent/employers/${id}`, payload);
      const updated = res.data.employer || res.data;
      setEmployer((prev) => ({ ...prev, ...updated }));
      closeEditEmployer();
    } catch (err) {
      console.error('Failed to update employer', err);
      setEditEmployerError(true);
    } finally {
      setEditEmployerSaving(false);
    }
  };

  const handleConvertToCandidate = async () => {
    setConvertingToCandidate(true);
    setConvertToCandidateError(false);
    try {
      await api.post(`/agent/employers/${id}/convert-to-job-seeker`, { call_note: convertToCandidateNote });
      setConvertToCandidateOpen(false);
      window.alert('Converted to job seeker. You can find them under Candidates.');
      navigate('/agent/employers');
    } catch (err) {
      console.error('Failed to convert employer', err);
      setConvertToCandidateError(true);
    } finally {
      setConvertingToCandidate(false);
    }
  };

  return (
    <AgentLayout>
      <div className="bg-gradient-to-br from-[#FAF5FC] to-[#FDF1F9] -m-6 p-6 min-h-screen">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/agent/employers" className="text-sm font-semibold text-purple-600 hover:text-pink-600 transition-colors">
          ← Back to Employers
        </Link>
      </div>

      {loading && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center text-gray-400">
          Loading employer…
        </div>
      )}

      {!loading && notFound && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center text-gray-400">
          Employer not found.
        </div>
      )}

      {!loading && forbidden && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center text-red-500">
          You don't have access to this employer.
        </div>
      )}

      {!loading && loadError && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center text-red-500">
          Could not load this employer.{' '}
          <button onClick={loadEmployer} className="underline font-medium">
            Retry
          </button>
        </div>
      )}

      {!loading && employer && !notFound && !forbidden && (
        <>
          {/* Header card */}
          <div className="bg-white rounded-2xl shadow-sm border border-purple-100 p-6 mb-6">
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div>
                <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent tracking-tight">{employer.name}</h2>
                <p className="text-sm text-gray-500 mt-1">
                  {employer.company_industry || 'Industry not specified'}
                  {employer.company_size ? ` · ${employer.company_size} employees` : ''}
                </p>
              </div>
                         <div className="flex gap-2">
                <button
                  onClick={openViewEmployer}
                  className="bg-gradient-to-r from-purple-600 to-pink-500 text-white text-xs font-semibold px-5 py-2 rounded-full shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md hover:brightness-110 active:scale-95"
                >
                  View
                </button>
                <button
                  onClick={openEditEmployer}
                  className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white text-xs font-semibold px-5 py-2 rounded-full shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md hover:brightness-110 active:scale-95"
                >
                  Edit
                </button>

               {/* --- Post Job button (verified employers only) --- */}
{employer.company_verified_status === 'verified' ? (
  <Link
    to={`/agent/employers/${id}/jobs/new`}
    className="bg-gradient-to-r from-purple-600 to-pink-500 text-white text-xs font-semibold px-5 py-2 rounded-full shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md hover:brightness-110 active:scale-95"
  >
    + Post Job
  </Link>
) : (
  <span
    title="Employer must be verified before you can post a job"
    className="bg-gray-100 text-gray-400 text-xs font-semibold px-5 py-2 rounded-full cursor-not-allowed"
  >
    + Post Job
  </span>
)}
  
                {employer.phone && (
                  <a
                    href={`tel:${employer.phone}`}
                    className="bg-gradient-to-r from-pink-500 to-rose-500 text-white text-xs font-semibold px-5 py-2 rounded-full shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md hover:brightness-110 active:scale-95"
                  >
                    Call
                  </a>
                )}
                {employer.phone && (
                  <a
                    href={`https://wa.me/91${employer.phone}`}
                    target="_blank"
                    rel="noreferrer"
                    className="bg-gradient-to-r from-emerald-500 to-green-600 text-white text-xs font-semibold px-5 py-2 rounded-full shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md hover:brightness-110 active:scale-95"
                  >
                    WhatsApp
                  </a>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 text-sm">
  <div className="min-w-0">
    <p className="text-gray-400 text-xs uppercase mb-1">Email</p>
    {employer.email ? (
      <a
        href={`mailto:${employer.email}`}
        className="text-purple-600 hover:text-pink-600 hover:underline break-all"
      >
        {employer.email}
      </a>
    ) : (
      <p className="text-[#0D1B3D]">—</p>
    )}
  </div>
  <div className="min-w-0">
    <p className="text-gray-400 text-xs uppercase mb-1">Phone</p>
    {employer.phone ? (
      <a
        href={`tel:${employer.phone}`}
        className="text-purple-600 hover:text-pink-600 hover:underline break-words"
      >
        {employer.phone}
      </a>
    ) : (
      <p className="text-[#0D1B3D]">—</p>
    )}
  </div>
  <div className="min-w-0">
    <p className="text-gray-400 text-xs uppercase mb-1">City</p>
    <p className="text-[#0D1B3D] break-words">{employer.city || '—'}</p>
  </div>
  <div className="min-w-0">
    <p className="text-gray-400 text-xs uppercase mb-1">Website</p>
    {employer.company_website ? (
      <a
        href={employer.company_website}
        target="_blank"
        rel="noreferrer"
        className="text-purple-600 hover:text-pink-600 hover:underline break-all"
      >
        {employer.company_website}
      </a>
    ) : (
      <p className="text-[#0D1B3D]">—</p>
    )}
  </div>
</div>
            {employer.company_about && (
              <div className="mt-4 pt-4 border-t border-gray-50">
                <p className="text-gray-400 text-xs uppercase mb-1">About</p>
                <p className="text-sm text-gray-600">{employer.company_about}</p>
              </div>
            )}
          </div>

          {/* Jobs */}
          <div className="bg-white rounded-2xl shadow-sm border border-purple-100 mb-6 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100">
              <h3 className="font-semibold text-[#0D1B3D] text-sm">
                Job Postings ({employer.jobs?.length || 0})
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-400 text-xs uppercase">
                    <th className="px-5 py-2 font-medium">Title</th>
                    <th className="px-5 py-2 font-medium">Location</th>
                    <th className="px-5 py-2 font-medium">Salary</th>
                    <th className="px-5 py-2 font-medium">Openings</th>
                    <th className="px-5 py-2 font-medium">Status</th>
                    <th className="px-5 py-2 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(!employer.jobs || employer.jobs.length === 0) && (
                    <tr>
                      <td colSpan={6} className="px-5 py-4 text-center text-gray-400">
                        No job postings yet.
                      </td>
                    </tr>
                  )}
                  {employer.jobs?.map((j) => (
                    <tr key={j.id} className="border-t border-gray-50">
                      <td className="px-5 py-2.5">{j.title}</td>
                      <td className="px-5 py-2.5">{j.location || '—'}</td>
                      <td className="px-5 py-2.5">
                        {j.salary_max ? fmtINR(j.salary_max) : fmtINR(j.salary_min)}
                      </td>
                      <td className="px-5 py-2.5">{j.openings ?? '—'}</td>
                      <td className="px-5 py-2.5 capitalize">{j.status || '—'}</td>
                      <td className="px-5 py-2.5">
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => openEditJob(j)}
                            className="text-[#2563EB] hover:underline text-xs font-semibold"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => openDeleteJob(j)}
                            className="bg-gradient-to-r from-pink-500 to-rose-500 text-white text-xs font-semibold px-4 py-1.5 rounded-full shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md hover:brightness-110 active:scale-95"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Candidates sent to this employer by this agent */}
          <div className="bg-white rounded-2xl shadow-sm border border-purple-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100">
              <h3 className="font-bold text-sm bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent">
                My Candidates for This Employer ({employer.candidates?.length || 0})
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
               <thead>
  <tr className="text-left text-purple-500 text-[10px] uppercase bg-purple-50/60">
    <th className="px-5 py-2 font-medium">Name</th>
    <th className="px-5 py-2 font-medium">Mobile</th>
    <th className="px-5 py-2 font-medium">Last Updated</th>
    <th className="px-5 py-2 font-medium">Action</th>
  </tr>
</thead>
<tbody>
  {(!employer.candidates || employer.candidates.length === 0) && (
    <tr>
      <td colSpan={4} className="px-5 py-4 text-center text-gray-400">
        You haven't sent any candidates to this employer yet.
      </td>
    </tr>
  )}
  {employer.candidates?.map((c) => (
    <tr key={c.id} className="border-t border-gray-50">
      <td className="px-5 py-2.5">{c.name}</td>
      <td className="px-5 py-2.5">{c.phone}</td>
      <td className="px-5 py-2.5">{fmtDate(c.updated_at)}</td>
      <td className="px-5 py-2.5">
        <button
          onClick={() => openApplications(c)}
          className="bg-gradient-to-r from-purple-600 to-pink-500 text-white text-xs font-semibold px-4 py-1.5 rounded-full shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md hover:brightness-110 active:scale-95"
        >
          View
        </button>
      </td>
    </tr>
  ))}
</tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Candidate applications modal */}
      {appsModalCandidate && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={closeApplications}
        >
          <div
            className="bg-white rounded-xl shadow-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeApplications}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 text-xl font-bold"
            >
              ×
            </button>

            <h3 className="text-lg font-bold text-[#0D1B3D] mb-1">
              {appsModalCandidate.name}'s Applications
            </h3>
            <p className="text-sm text-gray-500 mb-5">
              All jobs {appsModalCandidate.name} applied to at {employer?.name}.
            </p>

            {appsLoading && (
              <p className="text-center text-gray-400 py-8">Loading…</p>
            )}

            {!appsLoading && appsError && (
              <p className="text-center text-red-500 py-8">
                Could not load applications.{' '}
                <button onClick={() => openApplications(appsModalCandidate)} className="underline">
                  Retry
                </button>
              </p>
            )}

            {!appsLoading && !appsError && applications.length === 0 && (
              <p className="text-center text-gray-400 py-8">No applications found.</p>
            )}

            {!appsLoading && !appsError && applications.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-400 text-xs uppercase border-b border-gray-100">
                      <th className="py-2 pr-4 font-medium">Job</th>
                      <th className="py-2 pr-4 font-medium">Location</th>
                      <th className="py-2 pr-4 font-medium">Status</th>
                      <th className="py-2 pr-4 font-medium">Applied</th>
                    </tr>
                  </thead>
                  <tbody>
                    {applications.map((a) => (
                      <tr key={a.id} className="border-t border-gray-50">
                        <td className="py-2.5 pr-4 font-medium">{a.job_title}</td>
                        <td className="py-2.5 pr-4">{a.location || '—'}</td>
                        <td className="py-2.5 pr-4 capitalize">
                          {(a.status || 'applied').replace(/_/g, ' ')}
                        </td>
                        <td className="py-2.5 pr-4">{fmtDate(a.applied_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Edit job modal */}
      {editJob && editForm && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={closeEditJob}
        >
          <div
            className="bg-white rounded-xl shadow-lg max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeEditJob}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 text-xl font-bold"
            >
              ×
            </button>

            <h3 className="text-lg font-bold text-[#0D1B3D] mb-5">Edit Job Posting</h3>

            {editError && (
              <p className="text-sm text-red-500 mb-4">
                Could not save changes. Please try again.
              </p>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="relative">
                <label className="text-xs text-gray-400 uppercase mb-1 block">Title</label>
                <input
                  type="text"
                  value={editForm.title}
                  onChange={handleJobTitleChange}
                  onFocus={() => { if (jobTitleSuggestions.length > 0) setShowJobTitleSuggestions(true); }}
                  onBlur={() => setTimeout(() => setShowJobTitleSuggestions(false), 150)}
                  autoComplete="off"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                />
                {jobTitleLoading && (
                  <span className="absolute right-3 top-9 text-xs text-gray-400">…</span>
                )}
                {showJobTitleSuggestions && jobTitleSuggestions.length > 0 && (
                  <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {jobTitleSuggestions.map((s, i) => (
                      <li
                        key={i}
                        onMouseDown={() => pickJobTitleSuggestion(s)}
                        className="px-3 py-2 text-sm hover:bg-[#EFF6FF] cursor-pointer"
                      >
                        {s}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="relative">
                <label className="text-xs text-gray-400 uppercase mb-1 block">Company Name</label>
                <input
                  type="text"
                  value={editForm.company_name}
                  onChange={handleJobCompanyChange}
                  onFocus={() => { if (jobCompanySuggestions.length > 0) setShowJobCompanySuggestions(true); }}
                  onBlur={() => setTimeout(() => setShowJobCompanySuggestions(false), 150)}
                  autoComplete="off"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                />
                {jobCompanyLoading && (
                  <span className="absolute right-3 top-9 text-xs text-gray-400">…</span>
                )}
                {showJobCompanySuggestions && jobCompanySuggestions.length > 0 && (
                  <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {jobCompanySuggestions.map((s, i) => (
                      <li
                        key={i}
                        onMouseDown={() => pickJobCompanySuggestion(s)}
                        className="px-3 py-2 text-sm hover:bg-[#EFF6FF] cursor-pointer"
                      >
                        {s}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="relative">
                <label className="text-xs text-gray-400 uppercase mb-1 block">Location</label>
                <input
                  type="text"
                  value={editForm.location}
                  onChange={handleJobLocationChange}
                  onFocus={() => { if (jobLocationSuggestions.length > 0) setShowJobLocationSuggestions(true); }}
                  onBlur={() => setTimeout(() => setShowJobLocationSuggestions(false), 150)}
                  autoComplete="off"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                />
                {jobLocationLoading && (
                  <span className="absolute right-3 top-9 text-xs text-gray-400">…</span>
                )}
                {showJobLocationSuggestions && jobLocationSuggestions.length > 0 && (
                  <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {jobLocationSuggestions.map((s, i) => (
                      <li
                        key={i}
                        onMouseDown={() => pickJobLocationSuggestion(s)}
                        className="px-3 py-2 text-sm hover:bg-[#EFF6FF] cursor-pointer"
                      >
                        {s}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div>
                <label className="text-xs text-gray-400 uppercase mb-1 block">Contact Number</label>
                <input
                  type="tel"
                  value={editForm.contact_number}
                  onChange={handleEditChange('contact_number')}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="text-xs text-gray-400 uppercase mb-1 block">Work Mode</label>
                <select
                  value={editForm.work_mode}
                  onChange={handleEditChange('work_mode')}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm capitalize"
                >
                  {WORK_MODE_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt.replace(/_/g, ' ')}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-xs text-gray-400 uppercase mb-1 block">Job Type</label>
                <select
                  value={editForm.job_type}
                  onChange={handleEditChange('job_type')}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm capitalize"
                >
                  {JOB_TYPE_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt.replace(/_/g, ' ')}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-xs text-gray-400 uppercase mb-1 block">Work Shift</label>
                <select
                  value={editForm.work_shift}
                  onChange={handleEditChange('work_shift')}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm capitalize"
                >
                  {WORK_SHIFT_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt} shift
                    </option>
                  ))}
                </select>
              </div>

              <div className="relative">
                <label className="text-xs text-gray-400 uppercase mb-1 block">Skills Required</label>
                <input
                  type="text"
                  value={editForm.skills_required}
                  onChange={handleJobSkillsChange}
                  onFocus={() => { if (jobSkillsSuggestions.length > 0) setShowJobSkillsSuggestions(true); }}
                  onBlur={() => setTimeout(() => setShowJobSkillsSuggestions(false), 150)}
                  autoComplete="off"
                  placeholder="Comma separated"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                />
                {jobSkillsLoading && (
                  <span className="absolute right-3 top-9 text-xs text-gray-400">…</span>
                )}
                {showJobSkillsSuggestions && jobSkillsSuggestions.length > 0 && (
                  <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {jobSkillsSuggestions.map((s, i) => (
                      <li
                        key={i}
                        onMouseDown={() => pickJobSkillsSuggestion(s)}
                        className="px-3 py-2 text-sm hover:bg-[#EFF6FF] cursor-pointer"
                      >
                        {s}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div>
                <label className="text-xs text-gray-400 uppercase mb-1 block">Salary Min</label>
                <input
                  type="number"
                  value={editForm.salary_min}
                  onChange={handleEditChange('salary_min')}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="text-xs text-gray-400 uppercase mb-1 block">Salary Max</label>
                <input
                  type="number"
                  value={editForm.salary_max}
                  onChange={handleEditChange('salary_max')}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="text-xs text-gray-400 uppercase mb-1 block">Experience Required (yrs)</label>
                <input
                  type="number"
                  value={editForm.experience_required}
                  onChange={handleEditChange('experience_required')}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="text-xs text-gray-400 uppercase mb-1 block">Openings</label>
                <input
                  type="number"
                  value={editForm.openings}
                  onChange={handleEditChange('openings')}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="text-xs text-gray-400 uppercase mb-1 block">Status</label>
                <select
                  value={editForm.status}
                  onChange={handleEditChange('status')}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm capitalize"
                >
                  {JOB_STATUS_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="text-xs text-gray-400 uppercase mb-1 block">Benefits</label>
                <textarea
                  value={editForm.benefits}
                  onChange={handleEditChange('benefits')}
                  rows={2}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                />
              </div>

              <div className="md:col-span-2">
                <label className="text-xs text-gray-400 uppercase mb-1 block">Description</label>
                <textarea
                  value={editForm.description}
                  onChange={handleEditChange('description')}
                  rows={5}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={closeEditJob}
                className="text-sm font-medium text-gray-500 px-4 py-2 rounded-lg hover:bg-gray-50"
                disabled={editSaving}
              >
                Cancel
              </button>
              <button
                onClick={saveEditJob}
                disabled={editSaving}
                className="bg-gradient-to-r from-purple-600 to-pink-500 hover:opacity-90 text-white text-sm font-semibold px-6 py-2 rounded-full shadow-sm transition disabled:opacity-60"
              >
                {editSaving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete job confirmation modal */}
      {deleteJob && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={closeDeleteJob}
        >
          <div
            className="bg-white rounded-xl shadow-lg max-w-md w-full p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-[#0D1B3D] mb-2">Delete Job Posting</h3>
            <p className="text-sm text-gray-500 mb-1">
              Are you sure you want to delete <span className="font-medium">{deleteJob.title}</span>?
            </p>
            <p className="text-sm text-gray-400 mb-5">This action cannot be undone.</p>

            {deleteError && (
              <p className="text-sm text-red-500 mb-4">
                Could not delete this job. Please try again.
              </p>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={closeDeleteJob}
                className="text-sm font-medium text-gray-500 px-4 py-2 rounded-lg hover:bg-gray-50"
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteJob}
                disabled={deleting}
                className="text-sm font-medium text-white bg-red-600 px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-60"
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View employer details modal */}
      {viewingEmployer && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={closeViewEmployer}
        >
          <div
            className="bg-white rounded-xl shadow-lg max-w-lg w-full max-h-[80vh] overflow-y-auto p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeViewEmployer}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 text-xl font-bold"
            >
              ×
            </button>

            <h3 className="text-lg font-bold text-[#0D1B3D] mb-5">{employer.name}</h3>

            <div className="space-y-4 text-sm">
              <div>
                <p className="text-gray-400 text-xs uppercase mb-1">Email</p>
                <p className="text-[#0D1B3D] break-all">{employer.email || '—'}</p>
              </div>
              <div>
                <p className="text-gray-400 text-xs uppercase mb-1">Phone</p>
                <p className="text-[#0D1B3D]">{employer.phone || '—'}</p>
              </div>
              <div>
                <p className="text-gray-400 text-xs uppercase mb-1">City</p>
                <p className="text-[#0D1B3D]">{employer.city || '—'}</p>
              </div>
              <div>
                <p className="text-gray-400 text-xs uppercase mb-1">Industry</p>
                <p className="text-[#0D1B3D]">{employer.company_industry || '—'}</p>
              </div>
              <div>
                <p className="text-gray-400 text-xs uppercase mb-1">Company Size</p>
                <p className="text-[#0D1B3D]">{employer.company_size || '—'}</p>
              </div>
              <div>
                <p className="text-gray-400 text-xs uppercase mb-1">Website</p>
                {employer.company_website ? (
                  <a
                    href={employer.company_website}
                    target="_blank"
                    rel="noreferrer"
                    className="text-purple-600 hover:text-pink-600 hover:underline break-all"
                  >
                    {employer.company_website}
                  </a>
                ) : (
                  <p className="text-[#0D1B3D]">—</p>
                )}
              </div>
              <div>
                <p className="text-gray-400 text-xs uppercase mb-1">About</p>
                <p className="text-[#0D1B3D] whitespace-pre-wrap">{employer.company_about || '—'}</p>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={closeViewEmployer}
                className="text-sm font-medium text-gray-500 px-4 py-2 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
              <button
                onClick={() => { closeViewEmployer(); openEditEmployer(); }}
                className="bg-gradient-to-r from-purple-600 to-pink-500 hover:opacity-90 text-white text-sm font-semibold px-6 py-2 rounded-full shadow-sm transition"
              >
                Edit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit employer details modal */}
      {editEmployer && editEmployerForm && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={closeEditEmployer}
        >
          <div
            className="bg-white rounded-xl shadow-lg max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeEditEmployer}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 text-xl font-bold"
            >
              ×
            </button>

            <div className="mb-5">
              <h3 className="text-lg font-bold text-[#0D1B3D]">Edit Company Details</h3>
              <p className="text-xs text-gray-400 mt-0.5">Update {employer.name}'s company information</p>
            </div>

            {editEmployerError && (
              <p className="text-sm text-red-500 mb-4 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                Could not save changes. Please try again.
              </p>
            )}

            <h4 className="text-xs font-semibold uppercase tracking-wide text-[#2563EB] mb-3 pb-2 border-b border-gray-100">
              👤 Contact Details
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
              <div>
                <label className="text-xs text-gray-400 uppercase mb-1 block">Company / Contact Name</label>
                <input
                  type="text"
                  value={editEmployerForm.name}
                  onChange={handleEditEmployerChange('name')}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="text-xs text-gray-400 uppercase mb-1 block">Email</label>
                <input
                  type="email"
                  value={editEmployerForm.email}
                  onChange={handleEditEmployerChange('email')}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="text-xs text-gray-400 uppercase mb-1 block">Phone</label>
                <input
                  type="tel"
                  value={editEmployerForm.phone}
                  onChange={handleEditEmployerChange('phone')}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                />
              </div>
            </div>

            <h4 className="text-xs font-semibold uppercase tracking-wide text-[#2563EB] mb-3 pb-2 border-b border-gray-100">
              📍 Location & Company
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="relative">
                <label className="text-xs text-gray-400 uppercase mb-1 block">City</label>
                <input
                  type="text"
                  value={editEmployerForm.city}
                  onChange={handleCityInputChange}
                  onFocus={() => setShowCitySuggestions(true)}
                  onBlur={() => setTimeout(() => setShowCitySuggestions(false), 150)}
                  autoComplete="off"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                />
                {showCitySuggestions && (citySuggestions.length > 0 || cityLoading) && (
                  <div className="absolute z-10 left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-52 overflow-y-auto">
                    {cityLoading ? (
                      <p className="text-xs text-gray-400 px-3 py-2">Searching…</p>
                    ) : (
                      citySuggestions.map((s) => (
                        <button
                          key={s}
                          type="button"
                          onMouseDown={() => selectCitySuggestion(s)}
                          className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-[#2563EB]"
                        >
                          {s}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>

              <div className="relative">
  <label className="text-xs text-gray-400 uppercase mb-1 block">Industry</label>
  <input
    type="text"
    value={editEmployerForm.company_industry}
    onChange={handleIndustryInputChange}
    onFocus={() => { if (industrySuggestions.length > 0) setShowIndustrySuggestions(true); }}
    onBlur={() => setTimeout(() => setShowIndustrySuggestions(false), 150)}
    autoComplete="off"
    placeholder="e.g. IT Services"
    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
  />
  {showIndustrySuggestions && (industrySuggestions.length > 0 || industryLoading) && (
    <div className="absolute z-10 left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-52 overflow-y-auto">
      {industryLoading ? (
        <p className="text-xs text-gray-400 px-3 py-2">Searching…</p>
      ) : (
        industrySuggestions.map((s) => (
          <button
            key={s}
            type="button"
            onMouseDown={() => selectIndustrySuggestion(s)}
            className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-[#2563EB]"
          >
            {s}
          </button>
        ))
      )}
    </div>
  )}
</div>

              <div>
                <label className="text-xs text-gray-400 uppercase mb-1 block">Company Size</label>
                <select
                  value={editEmployerForm.company_size}
                  onChange={handleEditEmployerChange('company_size')}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="">Select size</option>
                  {['1-10', '11-50', '51-200', '201-500', '501-1000', '1000+'].map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>

            </div>

            <h4 className="text-xs font-semibold uppercase tracking-wide text-[#2563EB] mb-3 pb-2 border-b border-gray-100 mt-5">
              🌐 Online Presence
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="text-xs text-gray-400 uppercase mb-1 block">Website</label>
                <input
                  type="url"
                  value={editEmployerForm.company_website}
                  onChange={handleEditEmployerChange('company_website')}
                  placeholder="https://example.com"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                />
              </div>

              <div className="md:col-span-2">
                <label className="text-xs text-gray-400 uppercase mb-1 block">About</label>
                <textarea
                  value={editEmployerForm.company_about}
                  onChange={handleEditEmployerChange('company_about')}
                  rows={4}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                />
              </div>
            </div>

            <div className="flex items-center gap-3 mt-6">
              <button
                onClick={() => { setConvertToCandidateNote(''); setConvertToCandidateError(false); setConvertToCandidateOpen(true); }}
                className="mr-auto text-sm font-medium text-amber-700 hover:text-amber-800 px-3 py-2 rounded-lg hover:bg-amber-50"
                disabled={editEmployerSaving}
              >
                🔁 Convert to Job Seeker
              </button>
              <button
                onClick={closeEditEmployer}
                className="text-sm font-medium text-gray-500 px-4 py-2 rounded-lg hover:bg-gray-50"
                disabled={editEmployerSaving}
              >
                Cancel
              </button>
              <button
                onClick={saveEditEmployer}
                disabled={editEmployerSaving}
                className="text-sm font-medium text-white bg-[#2563EB] px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-60"
              >
                {editEmployerSaving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {convertToCandidateOpen && employer && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4" onClick={() => setConvertToCandidateOpen(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">Convert to Job Seeker</h3>
              <p className="text-xs text-gray-500 mt-1">
                {employer.name} ({employer.phone}) will move from Employers to Candidates. Phone and password stay the same. Existing job postings are left as is.
              </p>
            </div>
            <div className="px-6 py-4">
              {convertToCandidateError && <p className="text-red-500 text-sm mb-2">Could not convert. Please try again.</p>}
              <label className="text-xs text-gray-400 uppercase mb-1 block">Call note (optional)</label>
              <textarea
                rows={2}
                value={convertToCandidateNote}
                onChange={(e) => setConvertToCandidateNote(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none"
              />
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50">
              <button
                onClick={() => setConvertToCandidateOpen(false)}
                className="text-sm font-medium text-gray-500 hover:text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleConvertToCandidate}
                disabled={convertingToCandidate}
                className="bg-amber-600 hover:bg-amber-700 text-white px-5 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
              >
                {convertingToCandidate ? 'Converting…' : 'Convert to Job Seeker'}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </AgentLayout>
  );
}

export default AgentEmployerDetail;